'use client';
import AddToCuratedList from '@c/admin/AddToCuratedList';
import ProductAdminControls from '@c/admin/ProductAdminControls';
import ProductDetailsBanner from '@c/banners/ProductDetailsBanner';
import MXProductCard from '@c/cards/MXProductCard';
import ShareProductModal from '@c/modals/ShareProductModal';
import { ProductDetails } from '@c/ProductDetails';
import ProductMeetTheSeller from '@c/ProductMeetTheSeller';
import SellerVacationDisplay from '@c/SellerVacationDisplay';
import HomeSection from '@features/homepage/components/Home/components/HomeSection';
import { useQuery } from '@tanstack/react-query';
import Breadcrumb, { type BreadcrumbItem } from '@ui/Breadcrumb';
import Carousel from '@ui/Carousel';
import { viewContent } from '@util/analytics';
import { trackUserInteraction } from '@util/firestore/recommendations';
import { getReviewsByUid } from '@util/firestore/reviews';
import useSuggestedProducts from '@util/hooks/useSuggestedProducts';
import { formatTimestamp } from '@util/index';
import { useAttributionContext } from 'context/AttributionContext';
import { useAuth } from 'context/AuthContext';
import useBuyerActionButtons from 'features/shop/hooks/useBuyerActionButtons';
import type { ProductDocument } from 'models/product';
import type { PublicUserDocument } from 'models/user';
import { Montserrat } from 'next/font/google';
import Link from 'next/link';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import ConfirmFitment from './ConfirmFitment';
import ProductInfoTabs from './ProductInfoTabs';
import UniversalFitmentNotice from './UniversalFitmentNotice';
import { WarehouseProvider } from './WarehouseProvider';

const montserrat = Montserrat({
  subsets: ['latin'],
  weight: ['400', '500', '600', '700'],
});

export default function ProductDetailPage({
  product: initialProduct,
  breadcrumbs,
  seller,
  warehouseGroupTitle,
}: {
  product: ProductDocument;
  seller: PublicUserDocument | null;
  breadcrumbs: BreadcrumbItem[];
  isPreview?: boolean;
  warehouseGroupTitle?: string;
}) {
  const [product, setProduct] = useState<ProductDocument>(initialProduct);
  // if (product) {
  //   if (
  //     product.category === 'Bike Parts' &&
  //     !product.fitting_bikes?.bikes.length &&
  //     !product.disclaimer
  //   ) {
  //     //  cause im lazy
  //     product.disclaimer =
  //       'Please check the description and contact the seller for vehicle fitment compatibility.';
  //   }
  // }
  const { userDoc } = useAuth();
  const { getAttribution } = useAttributionContext();
  const relatedProductsRef = useRef<HTMLDivElement>(null);

  // const { data: variations = [] } = useQuery<ProductDocument[]>({
  //   queryKey: ['warehouse_items', product.integration_info?.remote_id],
  //   queryFn: async () => {
  //     if (product.integration_info?.type !== 'warehouse') return [];
  //     const warehouse_id = product.integration_info?.remote_id;
  //     if (!warehouse_id) return [];
  //     const snap = await getDocs(
  //       query(
  //         productsRef,
  //         where('integration_info.remote_id', '==', warehouse_id)
  //       )
  //     );
  //     return snap.docs.map((doc) => doc.data()) as ProductDocument[];
  //   },
  // });

  const {
    basedOnActivity,
    youMayAlsoLike,
    relatedProducts,
    moreProductsFromSeller,
    isLoading,
  } = useSuggestedProducts({ product });
  const { selectedVariations, setErrors, onSelection } =
    useBuyerActionButtons(product);

  // Calculate filtered related products for logged out users (excluding already shown products)
  const filteredRelatedProducts = useMemo(() => {
    if (userDoc || !relatedProducts?.length) return [];

    // Collect all product IDs that have been shown above
    const shownProductIds = new Set<string>();

    // Add products from Similar Items (first 20)
    relatedProducts?.slice(0, 20).forEach((p) => shownProductIds.add(p.id));

    // Add products from More from this seller
    moreProductsFromSeller?.forEach((p) => shownProductIds.add(p.id));

    // Add products from Inspired by your recent views
    basedOnActivity?.forEach((p) => shownProductIds.add(p.id));

    // Add products from You may also like
    youMayAlsoLike.data?.results?.forEach((p) => shownProductIds.add(p.id));

    // Filter out already shown products and get next 10
    // If we don't have enough after filtering, use products from index 20 onwards
    const filtered = relatedProducts
      .filter((p) => !shownProductIds.has(p.id))
      .slice(0, 10);

    // If filtered is empty, use products starting from index 20
    if (filtered.length === 0 && relatedProducts.length > 20) {
      return relatedProducts.slice(20, 30);
    }

    return filtered;
  }, [
    userDoc,
    relatedProducts,
    moreProductsFromSeller,
    basedOnActivity,
    youMayAlsoLike.data?.results,
  ]);

  useEffect(() => {
    viewContent(product);
    trackUserInteraction({
      pid: product.id,
      interaction: 'view',
      attribution: getAttribution(product.id),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  const { data: reviews, isLoading: isLoadingReviews } = useQuery({
    queryKey: ['reviews'],
    queryFn: () =>
      getReviewsByUid({
        uid: product.seller_id ?? '',
        reviewsPerPage: 10,
      }),
    enabled: !!product.seller_id,
  });

  const isParts = product.category.toLowerCase().includes('part');

  const handleListingSwap = (selectedProduct: ProductDocument) => {
    setProduct(selectedProduct);
    window.history.replaceState(null, '', `/product/${selectedProduct.slug}`);
  };

  return (
    <WarehouseProvider
      key={initialProduct.id}
      product={product}
      landingProduct={initialProduct}
      warehouseGroupTitle={warehouseGroupTitle}
      onSelectListing={handleListingSwap}
    >
      <>
        <Suspense>
          <ShareProductModal product={product} />
        </Suspense>

        <div className="w-full bg-white">
          <div
            className={`w-full py-8 sm:mx-auto sm:max-w-default ${montserrat.className}`}
          >
            <div
              className={`flex flex-col ${
                product.category === 'Riding Gear' ? 'gap-y-2' : 'gap-y-4'
              }`}
            >
              <div className="w-full">
                <div
                  className={`hidden sm:mx-auto sm:flex sm:max-w-content ${
                    product.category === 'Riding Gear' ? 'pb-0 pt-2' : 'sm:pt-4'
                  }`}
                >
                  <div
                    className={product.category === 'Riding Gear' ? 'pb-2' : ''}
                  >
                    <Breadcrumb onProductDetails items={breadcrumbs} />
                  </div>
                </div>
              </div>

              {isParts && (
                <>
                  {product.fitting_bikes?.universal ? (
                    <UniversalFitmentNotice product={product} compact />
                  ) : (
                    <>
                      {!product.fitting_bikes?.bikes ||
                      !product.fitting_bikes?.bikes.length ? (
                        <></>
                      ) : (
                        <div className="flex flex-col gap-4 p-4 sm:gap-2 sm:p-0">
                          <ConfirmFitment
                            product={product}
                            breadcrumbs={breadcrumbs}
                          />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              <ProductDetailsBanner product={product} />
              <div className="mx-auto flex w-full flex-col items-center justify-center gap-[0.8rem] lg:flex-row">
                {(!!userDoc?.roles?.support || !!userDoc?.roles?.warehouse) && (
                  <ProductAdminControls product={product} />
                )}
                {!!userDoc?.roles?.support ||
                !!userDoc?.roles?.curated_lists ? (
                  <AddToCuratedList product={product} />
                ) : null}
              </div>

              <div className="hidden w-full px-4 lg:block">
                {!!(userDoc?.roles?.support || userDoc?.roles?.warehouse) && (
                  <div className="flex w-full justify-between gap-[0.8rem] font-semibold">
                    <span>
                      <strong>Product id:</strong> {product.id}
                    </span>
                    <span>
                      <strong>Product created:</strong>{' '}
                      {formatTimestamp(product.created)}
                    </span>
                    {product.integration_info?.type === 'warehouse' && (
                      <span>
                        <strong>Warehouse ID:</strong>{' '}
                        {product.integration_info.remote_id}
                      </span>
                    )}
                    {!!product.sold_date && (
                      <span>
                        <strong>Product sold:</strong>
                        {formatTimestamp(product.sold_date)}
                      </span>
                    )}
                    {typeof product.fee_override === 'number' && (
                      <span>
                        <strong>Fee override:</strong> {product.fee_override}
                      </span>
                    )}
                    {product.sources
                      ?.filter(
                        (source) => source.collection !== 'boot_trade_in'
                      )
                      .map((source) => (
                        <span key={source.id}>
                          <strong>Listing Source:</strong>{' '}
                          <Link
                            target="_blank"
                            href={
                              source.collection === 'data_depot'
                                ? `/data-depot/${source.id}`
                                : source.collection === 'wps'
                                ? `https://www.wpsorders.com/wpsonline/o2POPOUT.pgm?ITEM=${encodeURIComponent(
                                    source.id
                                  )}`
                                : `/product/${source.id}`
                            }
                          >
                            {source.collection} {source.id}
                          </Link>
                        </span>
                      ))}
                  </div>
                )}
              </div>

              <div className="hidden w-full sm:block">
                <SellerVacationDisplay product={product} />
              </div>
            </div>

            <div className="mt-4">
              <ProductDetails
                product={product}
                key={product.id}
                seller={seller}
                breadcrumbs={breadcrumbs}
                selectedVariations={selectedVariations}
                onSelection={onSelection}
                setErrors={setErrors}
                onWarehouseSelection={handleListingSwap}
              />

              <div className="mt-[5rem]">
                <ProductInfoTabs
                  product={product}
                  breadcrumbs={breadcrumbs}
                  selectedVariations={selectedVariations}
                  onSelection={handleListingSwap}
                />
              </div>
            </div>
          </div>
          {/* {!!variations.length && (
          <div>
            <div
              className={`w-full py-8 sm:mx-auto sm:max-w-default ${montserrat.className}`}
            >
              <div className="mt-16 flex w-full justify-center">
                <div className="mt-16 w-full px-2 sm:text-left">
                  <PageSectionHeader
                    title="Variations"
                    titleClassName="!text-[2.5rem] mb-8"
                  />

                  {!!variations?.length && (
                    <>
                      <div className="relative mt-4 px-2 sm:px-0">
                        <CarouselNew
                          opts={{
                            slidesToScroll: 2,
                          }}
                        >
                          <CarouselContent>
                            {variations.slice(0, 10).map((product) => (
                              <CarouselItem
                                className="basis-[43%] sm:basis-auto"
                                key={product.id}
                              >
                                <MXProductCard
                                  product={product}
                                  attribution={{
                                    data_source: 'typesense',
                                    section: 'similar-items',
                                  }}
                                />
                              </CarouselItem>
                            ))}
                          </CarouselContent>
                          <CarouselPrevious />
                          <CarouselNext />
                        </CarouselNew>
                      </div>

                      <div className="relative mt-4 px-2 sm:px-0">
                        <CarouselNew
                          opts={{
                            slidesToScroll: 2,
                          }}
                        >
                          <CarouselContent>
                            {variations.slice(10).map((product) => (
                              <CarouselItem
                                className="basis-[43%] sm:basis-auto"
                                key={product.id}
                              >
                                <MXProductCard
                                  product={product}
                                  attribution={{
                                    data_source: 'typesense',
                                    section: 'similar-items',
                                  }}
                                />
                              </CarouselItem>
                            ))}
                          </CarouselContent>
                          <CarouselPrevious />
                          <CarouselNext />
                        </CarouselNew>
                      </div>
                    </>
                  )}
                  {isLoading && <SkeletonCarouselComponent />}
                </div>
              </div>
            </div>
          </div>
        )} */}
          {!!relatedProducts?.length && (
            <div
              ref={relatedProductsRef}
              className={`w-full ${montserrat.className}`}
            >
              <HomeSection
                title="Similar Items"
                titleClassName="!text-[2.5rem]"
                className="mt-32 px-4 sm:px-0"
                carouselWrapper
              >
                <div className="flex flex-col">
                  <Carousel
                    loading={isLoading}
                    basis="basis-[48%] sm:basis-auto"
                    items={
                      relatedProducts?.slice(0, 10).map((product) => (
                        <MXProductCard
                          key={product.id}
                          product={product}
                          attribution={{
                            data_source: 'typesense',
                            section: 'similar-items',
                          }}
                        />
                      )) ?? []
                    }
                  />

                  <div className="-mt-10 sm:-mt-0">
                    <Carousel
                      loading={isLoading}
                      basis="basis-[48%] sm:basis-auto"
                      items={
                        relatedProducts?.slice(10, 20).map((product) => (
                          <MXProductCard
                            key={product.id}
                            product={product}
                            attribution={{
                              data_source: 'typesense',
                              section: 'similar-items',
                            }}
                          />
                        )) ?? []
                      }
                    />
                  </div>
                </div>
              </HomeSection>
            </div>
          )}

          {seller && (
            <div className="mt-16 w-full bg-[#F6F6F6] pb-8 pt-16">
              <div className="flex w-full sm:mx-auto sm:max-w-default">
                <ProductMeetTheSeller
                  sellerDoc={seller}
                  reviews={reviews ?? []}
                  product_id={product.id}
                  isLoadingReviews={isLoadingReviews}
                />
              </div>
            </div>
          )}

          {!userDoc && filteredRelatedProducts?.length > 0 && (
            <div className={`w-full ${montserrat.className}`}>
              <HomeSection
                title="Related Products"
                titleClassName="!text-[2.5rem]"
                className="mt-16 px-4 sm:px-0"
                carouselWrapper
              >
                <Carousel
                  loading={isLoading}
                  basis="basis-[48%] sm:basis-auto"
                  items={filteredRelatedProducts.map((product) => (
                    <MXProductCard
                      key={product.id}
                      product={product}
                      attribution={{
                        data_source: 'firebase',
                        section: 'related-products',
                      }}
                    />
                  ))}
                />
              </HomeSection>
            </div>
          )}

          {!!moreProductsFromSeller?.length && (
            <div className={`w-full ${montserrat.className}`}>
              <HomeSection
                title="More from this seller"
                titleClassName="!text-[2.5rem]"
                className="mt-32 px-4 sm:px-0"
                fullWidth
                backgroundColor="bg-[#F6F6F6]"
                carouselWrapper
              >
                <Carousel
                  basis="basis-[48%] sm:basis-auto"
                  items={moreProductsFromSeller.map((product) => (
                    <MXProductCard
                      key={product.id}
                      product={product}
                      attribution={{
                        data_source: 'typesense',
                        section: 'more-from-seller',
                      }}
                    />
                  ))}
                />
              </HomeSection>
            </div>
          )}

          {!!basedOnActivity?.length && (
            <div className={`w-full ${montserrat.className}`}>
              <HomeSection
                title="Inspired by your recent views"
                titleClassName="!text-[2.5rem]"
                className="mt-32 px-4 sm:px-0"
                carouselWrapper
              >
                <Carousel
                  basis="basis-[48%] sm:basis-auto"
                  items={basedOnActivity.map((product) => (
                    <MXProductCard
                      key={product.id}
                      product={product}
                      attribution={{
                        data_source: 'typesense',
                        section: 'inspired-by-your-recent-views',
                      }}
                    />
                  ))}
                />
              </HomeSection>
            </div>
          )}

          {!!youMayAlsoLike.data?.results?.filter(
            (p) => !relatedProducts?.some((rp) => rp.id === p.id)
          )?.length && (
            <div className={`w-full ${montserrat.className}`}>
              <HomeSection
                title="You may also like"
                titleClassName="!text-[2.5rem]"
                className="mt-32 px-4 pb-8 sm:px-0"
                carouselWrapper
              >
                <Carousel
                  loading={youMayAlsoLike.isLoading}
                  basis="basis-[48%] sm:basis-auto"
                  items={youMayAlsoLike.data.results
                    .filter(
                      (p) => !relatedProducts?.some((rp) => rp.id === p.id)
                    )
                    .slice(0, userDoc === null ? undefined : 10)
                    .map((product) => (
                      <MXProductCard
                        key={product.id}
                        product={product}
                        attribution={{
                          data_source: 'typesense',
                          section: 'you-may-also-like',
                        }}
                      />
                    ))}
                />
              </HomeSection>
            </div>
          )}
        </div>
      </>
    </WarehouseProvider>
  );
}
